import React, { useMemo, useState } from 'react'
import {
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Pencil,
  Plus,
  Scissors,
  Trash2,
  Zap,
} from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import PlatformIcon from '@/components/PlatformIcon'
import {
  PLATFORMS,
  addDays,
  fieldCls,
  fmt,
  localToday,
  mondayOf,
  newDraft,
  type Draft,
} from './postHelpers'
import { PostForm } from './PostForm'
import {
  useCreatePlannedPostMutation,
  useDeletePlannedPostMutation,
  useGetMilestoneEventsQuery,
  useGetPlannerWeekQuery,
  useUpdatePlannedPostMutation,
  type PlannedPost,
} from '@/redux/collaborationsApi'

/**
 * One week of organic posts beside the deal deliverables: the goal (two a week, for consistent
 * content), each day with what's due and what's planned, and a quick
 * form to plan a post. Whoever planned a post (the athlete, their manager, the Creator Lab)
 * shows on it.
 */
export const PostPlanner: React.FC<{
  /** "your" in the athlete app; the athlete's first name when someone acts for them */
  whose?: string
  onChanged?: () => void
  /** 'week' leaves out the goal card, for screens that show the goal elsewhere */
  variant?: 'full' | 'week'
  /** Any day in the week to open on (this week if left out) */
  initialWeek?: string
  /** Open a piece's full plan (idea, pre-production, dates); without it, edits stay inline */
  onOpenPiece?: (p: PlannedPost) => void
}> = ({ whose, onChanged, variant = 'full', initialWeek, onOpenPiece }) => {
  const today = localToday()
  const [weekStart, setWeekStart] = useState(() => mondayOf(initialWeek ?? today))
  const weekEnd = addDays(weekStart, 6)
  const { data, isLoading } = useGetPlannerWeekQuery(weekStart)
  const { data: dealData } = useGetMilestoneEventsQuery({
    start: new Date(`${weekStart}T00:00:00`).toISOString(),
    end: new Date(`${addDays(weekEnd, 1)}T00:00:00`).toISOString(),
  })
  const [create, { isLoading: saving }] = useCreatePlannedPostMutation()
  const [update] = useUpdatePlannedPostMutation()
  const [remove] = useDeletePlannedPostMutation()
  const [draftFor, setDraftFor] = useState<string | null>(null) // a day, or a post id when editing
  const [draft, setDraft] = useState<Draft | null>(null)
  const [postingId, setPostingId] = useState<string | null>(null)
  const [postUrl, setPostUrl] = useState('')

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  )
  const posts = data?.posts ?? []
  const deals = (dealData?.events ?? []) as { date: string; title: string; type: string }[]
  const summary = data?.summary
  const themes = data?.themes ?? []
  const thisWeek = weekStart === mondayOf(today)
  const postedThisWeek = posts.filter((p) => p.status === 'posted').length
  const plannedLeft = posts.filter((p) => p.status === 'planned' && p.plannedOn >= today).length
  const goal = summary?.goal ?? 2
  const openDays = days.filter(
    (d) => d >= today && !deals.some((e) => e.date === d) && !posts.some((p) => p.plannedOn === d),
  )

  const done = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn()
      toast(msg)
      onChanged?.()
      return true
    } catch (err: any) {
      toast.error('That didn’t save', { description: err?.data?.error ?? err?.message })
      return false
    }
  }

  const startNew = (day: string) => {
    setDraftFor(day)
    setDraft(newDraft(day, themes))
  }
  const startEdit = (p: PlannedPost) => {
    setDraftFor(p.id)
    setDraft({
      plannedOn: p.plannedOn,
      plannedTime: p.plannedTime ?? '',
      platform: p.platform,
      theme: p.theme,
      idea: p.idea ?? '',
    })
  }
  // draftFor is a day (YYYY-MM-DD) for a new post, or the id of the post being edited
  const isEditing = !!draftFor && !/^\d{4}-\d{2}-\d{2}$/.test(draftFor)
  const save = async (draft: Draft) => {
    const body = {
      plannedOn: draft.plannedOn,
      plannedTime: draft.plannedTime || null,
      platform: draft.platform,
      theme: draft.theme,
      idea: draft.idea.trim() || null,
    }
    const editing = isEditing
    const ok = await done(
      () => (editing ? update({ id: draftFor!, ...body }).unwrap() : create(body).unwrap()),
      editing ? 'Post updated' : 'Post planned',
    )
    if (ok) {
      setDraftFor(null)
      setDraft(null)
    }
  }

  const form = draft ? (
    <PostForm
      key={draftFor ?? ''}
      initial={draft}
      themes={themes}
      saving={saving}
      submitLabel={isEditing ? 'Save' : 'Plan it'}
      onSubmit={save}
      onCancel={() => {
        setDraftFor(null)
        setDraft(null)
      }}
    />
  ) : null

  const postRow = (p: PlannedPost) => {
    const posted = p.status === 'posted'
    if (draftFor === p.id) return <React.Fragment key={p.id}>{form}</React.Fragment>
    return (
      <div
        key={p.id}
        className={cn(
          'rounded-xl border p-3',
          posted ? 'border-[#C9E9A6] bg-[#F7FEE7]' : 'border-[#BFE6F0] bg-[#F0FAFC]',
        )}
      >
        <div className="flex items-start gap-2.5">
          <span
            className={cn(
              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
              posted ? 'bg-[#C0FD72] text-[#0F172A]' : 'bg-[#CFF1F8] text-[#0E7490]',
            )}
          >
            {posted ? (
              <Check className="h-4 w-4" strokeWidth={3} />
            ) : (
              <PlatformIcon platform={p.platform} className="h-3.5 w-3.5" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">
              {p.theme?.label ?? 'Post'}
              <span className="font-normal text-muted-foreground">
                {' '}
                · {PLATFORMS.find((x) => x.id === p.platform)?.label}
                {p.plannedTime ? ` · ${p.plannedTime}` : ''}
              </span>
            </p>
            {p.idea ? <p className="text-sm text-foreground">{p.idea}</p> : null}
            {p.hook ? <p className="text-sm italic text-muted-foreground">“{p.hook}”</p> : null}
            {!posted && (p.shootOn || p.editOn) ? (
              <p className="mt-1 flex flex-wrap gap-1.5 text-xs">
                {p.shootOn ? (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold',
                      p.shotAt
                        ? 'bg-[#E8F9D2] text-[#2F5E06]'
                        : p.shootOn < today
                          ? 'bg-[#FEF3C7] text-[#92400E]'
                          : 'bg-[#CCFBF1] text-[#0F766E]',
                    )}
                  >
                    {p.shotAt ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                    Shoot {fmt(p.shootOn, { weekday: 'short' })}
                  </span>
                ) : null}
                {p.editOn ? (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold',
                      p.editedAt
                        ? 'bg-[#E8F9D2] text-[#2F5E06]'
                        : p.editOn < today
                          ? 'bg-[#FEF3C7] text-[#92400E]'
                          : 'bg-[#DBEAFE] text-[#1D4ED8]',
                    )}
                  >
                    {p.editedAt ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                    Edit {fmt(p.editOn, { weekday: 'short' })}
                  </span>
                ) : null}
              </p>
            ) : null}
            <p className="mt-0.5 text-xs text-muted-foreground">
              {posted ? (
                <>
                  Posted {p.postedOn ? fmt(p.postedOn, { weekday: 'short' }) : ''}
                  {p.postUrl ? (
                    <a
                      href={p.postUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-1.5 inline-flex items-center gap-0.5 font-semibold text-[#3F7A0A] underline"
                    >
                      View <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                </>
              ) : p.plannedBy.role === 'manager' ? (
                `Planned by ${p.plannedBy.name ?? 'your manager'} (manager)`
              ) : p.plannedBy.role === 'creator_lab' ? (
                'Planned by the Athios Creator Lab'
              ) : null}
            </p>
          </div>
        </div>
        {!posted ? (
          postingId === p.id ? (
            <div className="mt-2 space-y-2">
              <input
                value={postUrl}
                onChange={(e) => setPostUrl(e.target.value)}
                placeholder="Link to the post (optional)"
                inputMode="url"
                className={fieldCls}
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await done(
                      () =>
                        update({
                          id: p.id,
                          status: 'posted',
                          postedOn: today,
                          postUrl: postUrl.trim() || null,
                        }).unwrap(),
                      'Nice. That counts toward this week',
                    )
                    if (ok) {
                      setPostingId(null)
                      setPostUrl('')
                    }
                  }}
                  className="h-10 rounded-xl bg-[#C0FD72] text-sm font-semibold text-[#0F172A]"
                >
                  It’s up
                </button>
                <button
                  type="button"
                  onClick={() => setPostingId(null)}
                  className="h-10 rounded-xl border border-border bg-card text-sm font-semibold text-foreground"
                >
                  Not yet
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setPostingId(p.id)
                  setPostUrl('')
                }}
                className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#0F172A] text-sm font-semibold text-[#FFFFFF]"
              >
                <Check className="h-4 w-4" /> I posted it
              </button>
              <button
                type="button"
                aria-label={onOpenPiece ? 'Open the plan' : 'Edit'}
                onClick={() => (onOpenPiece ? onOpenPiece(p) : startEdit(p))}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card text-foreground"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Remove"
                onClick={() => done(() => remove(p.id).unwrap(), 'Removed')}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card text-foreground"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )
        ) : (
          <button
            type="button"
            onClick={() =>
              done(() => update({ id: p.id, status: 'planned' }).unwrap(), 'Marked not posted')
            }
            className="mt-1.5 text-xs font-semibold text-muted-foreground underline"
          >
            Undo
          </button>
        )}
      </div>
    )
  }

  return (
    <section className="space-y-3">
      {/* The goal */}
      {variant === 'full' ? (
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {thisWeek ? 'This week' : 'That week'}
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                {postedThisWeek} of {goal} posts
              </p>
            </div>
            <div className="flex gap-1.5" aria-label="Last weeks">
              {(summary?.weeks ?? [])
                .slice(0, 4)
                .reverse()
                .map((w) => (
                  <span
                    key={w.weekStart}
                    title={`Week of ${fmt(w.weekStart, { month: 'short', day: 'numeric' })}: ${w.posted} posted`}
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold',
                      w.posted >= goal
                        ? 'bg-[#C0FD72] text-[#0F172A]'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {w.posted}
                  </span>
                ))}
            </div>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[#C0FD72] transition-all"
              style={{ width: `${Math.min(100, (postedThisWeek / goal) * 100)}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {thisWeek
              ? postedThisWeek >= goal
                ? `Goal hit. Extra posts still help ${whose ? 'them' : 'you'} grow.`
                : `Post ${goal - postedThisWeek} more by Sunday to stay on pace.`
              : 'Two posts a week keeps the content consistent.'}
            {thisWeek && postedThisWeek + plannedLeft < goal && openDays.length
              ? ` ${fmt(openDays[0], { weekday: 'long' })}${openDays[1] ? ` and ${fmt(openDays[1], { weekday: 'long' })}` : ''} ${openDays[1] ? 'are' : 'is'} open.`
              : ''}
          </p>
        </div>
      ) : null}

      {/* The week */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous week"
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-bold text-foreground">
          {fmt(weekStart, { month: 'short', day: 'numeric' })} –{' '}
          {fmt(weekEnd, { month: 'short', day: 'numeric' })}
        </p>
        <button
          type="button"
          aria-label="Next week"
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card text-foreground"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      <ol className="space-y-2">
        {days.map((d) => {
          const dayDeals = deals.filter((e) => e.date === d)
          const dayPosts = posts.filter(
            (p) => (p.status === 'posted' ? (p.postedOn ?? p.plannedOn) : p.plannedOn) === d,
          )
          // Production work on this day for posts on other days
          const dayTasks = posts.flatMap((p) =>
            p.status === 'posted' || p.plannedOn === d
              ? []
              : [
                  ...(p.shootOn === d ? [{ kind: 'shoot' as const, p, done: !!p.shotAt }] : []),
                  ...(p.editOn === d ? [{ kind: 'edit' as const, p, done: !!p.editedAt }] : []),
                ],
          )
          const past = d < today
          return (
            <li
              key={d}
              className={cn(
                'rounded-2xl border bg-card p-3 shadow-sm',
                d === today ? 'border-[#C9E9A6]' : 'border-border',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <p
                  className={cn(
                    'text-sm font-bold',
                    past ? 'text-muted-foreground' : 'text-foreground',
                  )}
                >
                  {fmt(d, { weekday: 'short' })} {fmt(d, { day: 'numeric' })}
                  {d === today ? (
                    <span className="ml-1.5 rounded-full bg-[#E8F9D2] px-2 py-0.5 text-xs font-bold text-[#2F5E06]">
                      Today
                    </span>
                  ) : null}
                </p>
                {!past && draftFor !== d ? (
                  <button
                    type="button"
                    onClick={() => startNew(d)}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#3F7A0A]"
                  >
                    <Plus className="h-4 w-4" /> Plan a post
                  </button>
                ) : null}
              </div>
              {dayDeals.length ? (
                <ul className="mt-2 space-y-1">
                  {dayDeals.map((e, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-1.5 rounded-lg bg-[#FFF4E5] px-2.5 py-1.5 text-xs font-semibold text-[#92400E]"
                    >
                      <Zap className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{e.title}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {dayTasks.length ? (
                <ul className="mt-2 space-y-1">
                  {dayTasks.map(({ kind, p, done }) => (
                    <li key={`${kind}-${p.id}`}>
                      <button
                        type="button"
                        onClick={() => onOpenPiece?.(p)}
                        className={cn(
                          'flex w-full items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-left text-xs font-semibold',
                          done
                            ? 'bg-[#E8F9D2] text-[#2F5E06]'
                            : past
                              ? 'bg-[#FEF3C7] text-[#92400E]'
                              : kind === 'shoot'
                                ? 'bg-[#CCFBF1] text-[#0F766E]'
                                : 'bg-[#DBEAFE] text-[#1D4ED8]',
                        )}
                      >
                        {done ? (
                          <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
                        ) : kind === 'shoot' ? (
                          <Camera className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <Scissors className="h-3.5 w-3.5 shrink-0" />
                        )}
                        <span className="truncate">
                          {!done && past ? 'Missed: ' : ''}
                          {kind === 'shoot' ? 'Shoot' : 'Edit'}:{' '}
                          {p.idea ?? p.theme?.label ?? 'post'}
                          {kind === 'shoot' && p.shootTime ? ` · ${p.shootTime}` : ''}
                          {kind === 'shoot' && p.location ? ` · ${p.location}` : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {dayPosts.length ? (
                <div className="mt-2 space-y-2">{dayPosts.map(postRow)}</div>
              ) : null}
              {draftFor === d ? form : null}
              {!dayDeals.length &&
              !dayPosts.length &&
              !dayTasks.length &&
              draftFor !== d &&
              !past ? (
                <p className="mt-1 text-xs text-muted-foreground">Open day</p>
              ) : null}
            </li>
          )
        })}
      </ol>
    </section>
  )
}

export default PostPlanner
